"use client";

import clsx from "clsx";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { AudioProvider, useAudio } from "@/app/_providers/AudioProvider";
import { appUrls } from "@/constants";
import { TTSModels } from "@mw/shared/models";
import { ttsLanguages as languages, ttsLanguagesMap } from "@components/languages/config";
import { Button } from "@components/core/Button";
import { DottedBorder } from "@ui/components/core/GradientBorder";
import { LinkTracked } from "@ui/components/core/LinkTracked";
import { OmniboxSimpleWrapper } from "@ui/components/demos/OmniboxSimpleWrapper";
import { useOmniboxStateOptional } from "@ui/components/demos/OmniboxStateContext";
import { TextEditor } from "@/app/_blocks/BlockInteractCreateOmnibox/TextEditor";
import { TextHighlighter } from "@/app/_blocks/BlockInteractCreateOmnibox/TextHighlighter";
import { LimitReachedOverlay } from "@/app/_blocks/BlockInteractCreateOmnibox/LimitReachedOverlay";
import { LanguageSelect, DownloadButton } from "@ui/components/demos/TTS/components/TTSControls";
import { CHARACTER_LIMIT } from "@ui/components/demos/TTS/constants";
import { getDefaultPrompt } from "@ui/components/languages/textPrompts";
import { useIsMobile } from "@ui/hooks/useBreakpoint";
import { useTtsGeneration } from "@ui/hooks/useTtsGeneration";
import { ChevronLeft } from "lucide-react";
import { LocalStorageKeys, safeLocalStorage } from "@utilities/localStorage";
import { logger } from "@utilities/logger";
import { useLocale, useTranslations } from "next-intl";
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type Ref } from "react";

import type { VoiceWithPreview } from "./types";
import { getPreviewUrlForLanguage } from "./types";

import { getVoicesWithPreview } from "@/app/_api/fetchVoices";
import { TTSDownloadSignUpPopover } from "@ui/components/demos/SliderGenerateTTSDemo/TTSDownloadSignUpPopover";
import { PlayButton } from "./PlayButton";
import { VoiceListPanel } from "./VoiceListPanel";
import { VoiceChip } from "./VoiceChip";

export interface VoicesWithTTSHandle {
  stopAudio: () => void;
}

export interface VoicesWithTTSProps {
  defaultLanguage?: string;
  displayCategory?: boolean;
  downloadButton?: "show" | "hide";
  initialPrompt?: { text: string; title?: string };
  initialSelectedVoiceId?: string;
  showBorder?: boolean;
  showSignUpPopoverOnDownload?: boolean;
  mobileEditorOnly?: boolean;
  onAfterDownload?: () => void;
  onBackOverride?: () => void;
  pagination?: "show" | "hide" | "on-hover";
  placeholder?: string;
  shadow?: boolean;
  size?: string;
  voiceDescriptions?: "show" | "hide";
  voices?: VoiceWithPreview[];
}

const DEFAULT_TEXT = "Hello! This is a preview of the selected voice. You can edit this text and generate speech.";

type MobileStep = "voices" | "editor";

function sortLanguagesWithEnglishFirst(a: (typeof languages)[0], b: (typeof languages)[0]) {
  if (a.value === "en") return -1;
  if (b.value === "en") return 1;
  return a.label.localeCompare(b.label);
}

const CharacterCounter = ({
  text,
  className,
  maxCharacters,
}: {
  className?: string;
  maxCharacters: number;
  text: string;
}) => {
  const isNearLimit = text.length > maxCharacters * 0.8;

  return (
    <div
      className={clsx(
        "tw-z-10 tw-pointer-events-none tw-sticky tw-bottom-4 tw-right-4 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-px-2 tw-py-0.5 tw-transition-opacity tw-duration-200 shadow-xs",
        isNearLimit ? "tw-opacity-100 bg-negative" : "tw-opacity-0",
        text.length > maxCharacters && "bg-red text-inverse",
        className,
      )}
    >
      <span className='tw-tabular-nums f-ui-05'>
        {text.length}/{maxCharacters}
      </span>
    </div>
  );
};

function VoicesWithTTSInner({
  ref,
  defaultLanguage,
  displayCategory = false,
  downloadButton = "show",
  showBorder = false,
  showSignUpPopoverOnDownload = false,
  initialPrompt,
  initialSelectedVoiceId,
  mobileEditorOnly = false,
  onAfterDownload,
  onBackOverride,
  pagination = "show",
  placeholder,
  shadow = false,
  voiceDescriptions = "show",
  voices: initialVoices = [],
}: VoicesWithTTSProps & { ref?: Ref<VoicesWithTTSHandle> }) {
  const locale = useLocale();
  const tTTS = useTranslations("ClientKeys.TTS");
  const tVoices = useTranslations("ClientKeys.VoicesWithTTS");
  const audioElementRef = useRef<HTMLAudioElement>(null);
  const prevLanguageRef = useRef<string | undefined>(undefined);
  const userPausedPreviewRef = useRef(false);
  const initialVoicesRef = useRef(initialVoices);
  const generateCountRef = useRef(0);

  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage ?? locale);
  const [voices, setVoices] = useState<VoiceWithPreview[]>(initialVoices);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<VoiceWithPreview | undefined>(
    (initialSelectedVoiceId && initialVoices.find(v => v.voiceId === initialSelectedVoiceId)) || initialVoices[0],
  );
  const [text, setText] = useState(initialPrompt?.text ?? DEFAULT_TEXT);
  const [displayCharacters, setDisplayCharacters] = useState<string[]>([]);
  const [mobileStep, setMobileStep] = useState<MobileStep>(mobileEditorOnly ? "editor" : "voices");
  const [isSignUpPopoverOpen, setIsSignUpPopoverOpen] = useState(false);

  const isMobile = useIsMobile();

  const language = ttsLanguagesMap[selectedLanguage];
  const sortedLanguages = useMemo(() => languages.sort(sortLanguagesWithEnglishFirst), []);

  const {
    generate,
    playingState,
    setPlayingState,
    alignment,
    regenerate,
    setRegenerate,
    showLimitReachedOverlay,
    completeAudioData,
  } = useTtsGeneration(DEFAULT_TEXT, selectedVoice, 1, true, "tts_omnibox");

  const { playingId, play, stop } = useAudio();
  const omniboxState = useOmniboxStateOptional();

  // Helper functions to manage omnibox state
  const setOmniboxActive = useCallback(() => {
    omniboxState?.setState("active");
  }, [omniboxState]);

  const setOmniboxIdle = useCallback(() => {
    omniboxState?.setState("idle");
  }, [omniboxState]);

  const pause = useCallback(() => {
    if (!audioElementRef.current) return;
    audioElementRef.current.pause();
    setPlayingState("paused");
    setOmniboxIdle();
  }, [setPlayingState, setOmniboxIdle]);

  const handlePreviewStart = useCallback(() => {
    pause();
    setOmniboxActive();
  }, [pause, setOmniboxActive]);

  const handlePreviewPauseOrStop = useCallback(() => {
    userPausedPreviewRef.current = true;
    if (playingState !== "playing") {
      setOmniboxIdle();
    }
  }, [playingState, setOmniboxIdle]);

  useEffect(() => {
    if (voices.length > 0 && !selectedVoice) {
      setSelectedVoice(voices[0]);
    }
  }, [voices, selectedVoice]);

  useImperativeHandle(
    ref,
    () => ({
      stopAudio: () => {
        stop();
        pause();
      },
    }),
    [stop, pause],
  );

  const fetchVoicesForCurrentLanguage = useCallback(
    async (languageCode: string) => {
      setVoicesLoading(true);
      try {
        const fetchedVoices = await getVoicesWithPreview(languageCode);
        // Mirror SpeechInner's onLoad pattern: if no voices returned, fall back to initial voices
        const resolvedVoices = fetchedVoices.length > 0 ? fetchedVoices : initialVoicesRef.current;
        setVoices(resolvedVoices);
        if (!resolvedVoices.find(voice => voice.voiceId === selectedVoice?.voiceId)) {
          setSelectedVoice(resolvedVoices[0]);
        }
      } catch {
        // On error, fall back to initial voices
        setVoices(initialVoicesRef.current);
        setSelectedVoice(initialVoicesRef.current[0]);
      }
      setVoicesLoading(false);
    },
    [selectedVoice?.voiceId],
  );

  function playAudio() {
    if (!audioElementRef.current) return;
    stop();
    audioElementRef.current.play();
    setPlayingState("playing");
    setOmniboxActive();
  }

  async function handleGenerate() {
    if (!audioElementRef.current) return;
    stop();
    setDisplayCharacters(text.split(""));
    await generate({
      textPrompt: text,
      audioElement: audioElementRef.current,
      modelId: TTSModels.ELEVEN_V3,
      voice: selectedVoice,
    });
    // Only count successful generations (failed ones leave the audio element paused at 0)
    if (audioElementRef.current.paused && audioElementRef.current.currentTime === 0) return;
    generateCountRef.current += 1;
    if (
      showSignUpPopoverOnDownload &&
      generateCountRef.current >= 2 &&
      !safeLocalStorage.getItem(LocalStorageKeys.TTS_SIGNUP_POPOVER_DISMISSED)
    ) {
      setIsSignUpPopoverOpen(true);
    }
  }

  function handlePlayPause() {
    if (playingState === "playing" || playingState === "loading") {
      pause();
    } else {
      playAudio();
    }
  }

  function handlePlay() {
    if (regenerate) {
      handleGenerate();
    } else {
      handlePlayPause();
    }
  }
  const updateDefaultPrompt = useCallback((languageCode: string) => {
    getDefaultPrompt(languageCode, TTSModels.ELEVEN_V3)
      .then(prompt => {
        if (prompt?.text) {
          setText(prompt.text);
        }
      })
      .catch(error => {
        logger.error(error instanceof Error ? error : null, "Error getting default prompt");
      });
  }, []);

  const handleLanguageChange = useCallback(
    async (languageCode: string) => {
      audioElementRef.current?.pause();
      pause();
      prevLanguageRef.current = selectedLanguage;

      await fetchVoicesForCurrentLanguage(languageCode);
      setSelectedLanguage(languageCode);
      updateDefaultPrompt(languageCode);
      setRegenerate(true);

      captureEvent(EventName.mw_tts_language_changed, {
        source_component: "voices_with_tts_omnibox",
        language: languageCode,
        previous_language: selectedLanguage,
      });
    },
    [setRegenerate, pause, selectedLanguage, updateDefaultPrompt, fetchVoicesForCurrentLanguage],
  );

  const handleTextChange = useCallback(
    (newText: string) => {
      setText(newText);
      setRegenerate(true);
      pause();
    },
    [setRegenerate, pause],
  );

  const handleVoiceSelect = useCallback(
    (voice: VoiceWithPreview) => {
      setSelectedVoice(voice);
      setRegenerate(true);
      pause();

      // Auto-play preview if user hasn't manually paused before
      if (!userPausedPreviewRef.current) {
        const willPlay = playingId !== voice.voiceId;
        if (willPlay) {
          captureEvent(EventName.mw_tts_voice_heard, {
            source_component: "voices_with_tts_omnibox",
            audio_type: "preview",
            voice_id: voice.voiceId,
            voice_name: voice.name,
            trigger: "voice_selection",
          });
        }
        const previewUrl = getPreviewUrlForLanguage(voice, selectedLanguage);
        play(voice.voiceId, previewUrl);
        handlePreviewStart();
      }

      captureEvent(EventName.mw_tts_voice_changed, {
        source_component: "voices_with_tts_omnibox",
        voice_id: voice.voiceId,
        voice_name: voice.name,
        previous_voice_id: selectedVoice?.voiceId,
        previous_voice_name: selectedVoice?.name,
      });
    },
    [setRegenerate, pause, selectedVoice, play, handlePreviewStart, selectedLanguage, playingId],
  );

  const isPlayButtonDisabled = !text.trim() || !selectedVoice || playingState === "loading";

  return (
    <>
      <div className='tw-relative tw-flex tw-flex-col tw-h-[29rem] md:tw-h-[28rem]'>
        <div
          className={clsx(
            "tw-relative tw-flex-1 tw-min-h-0 tw-flex tw-flex-col",
            showBorder &&
              "tw-rounded-3xl tw-shadow-[0_0_0_1px_rgba(0,0,0,0.06),0_1px_2px_0_rgba(0,0,0,0.04),0_2px_4px_0_rgba(0,0,0,0.04)]",
          )}
        >
          <OmniboxSimpleWrapper
            initiallyVisible={true}
            className={clsx(
              "tw-relative tw-isolate tw-flex-1 tw-min-h-0 !tw-rounded-3xl tw-overflow-hidden",
              shadow && "tw-shadow-md tw-z-10",
            )}
          >
            <div
              className={clsx(
                "tw-flex tw-h-full tw-transition-transform tw-duration-200 tw-ease-out",
                mobileEditorOnly ? "tw-w-full" : "tw-w-[200%] md:tw-w-full",
                !mobileEditorOnly && (mobileStep === "editor" ? "-tw-translate-x-1/2" : "tw-translate-x-0"),
                "md:tw-translate-x-0",
              )}
            >
              <div
                className={clsx(
                  "tw-relative tw-flex tw-flex-col",
                  "tw-p-4 md:tw-p-7",
                  "tw-w-1/2 md:tw-w-1/2",
                  mobileEditorOnly && "tw-hidden md:tw-flex",
                )}
                inert={showLimitReachedOverlay || (isMobile && mobileStep === "editor" ? true : undefined)}
              >
                <VoiceListPanel
                  displayCategory={displayCategory}
                  voices={voices}
                  selectedVoice={selectedVoice}
                  onVoiceSelect={handleVoiceSelect}
                  onContinue={() => {
                    setMobileStep("editor");
                    captureEvent(EventName.mw_tts_edit_text);
                  }}
                  loading={voicesLoading}
                  onPreviewPause={handlePreviewPauseOrStop}
                  onPreviewStart={handlePreviewStart}
                  selectedLanguage={selectedLanguage}
                  pagination={pagination}
                  voiceDescriptions={voiceDescriptions}
                />

                <DottedBorder side='right' className='tw-hidden md:tw-block' />
              </div>

              <div
                className={clsx(
                  "tw-relative tw-isolate tw-flex tw-flex-col tw-min-w-0",
                  "tw-p-4 md:tw-px-7 md:tw-pt-7 md:tw-pb-7",
                  mobileEditorOnly ? "tw-w-full md:tw-w-1/2" : "tw-w-1/2 md:tw-w-1/2",
                )}
                inert={showLimitReachedOverlay || (isMobile && mobileStep === "voices" ? true : undefined)}
              >
                {/* Desktop header */}
                <div className='tw-hidden md:tw-flex tw-items-center tw-justify-between tw-mb-2 md:tw-mb-3 tw-p-px'>
                  <h3 className='tw-opacity-60 f-paragraph-03'>{tVoices("enter_your_text")}</h3>
                </div>

                <div className='tw-mb-3 md:tw-hidden tw-min-w-0'>
                  <div className='tw-flex tw-items-center tw-gap-2 tw-mb-3'>
                    <Button
                      variant='elevated'
                      size='pill'
                      onClick={onBackOverride ?? (() => setMobileStep("voices"))}
                      disabled={!selectedVoice}
                      aria-label={tVoices("back_to_voice_selection")}
                    >
                      <ChevronLeft className='tw-size-4' />
                    </Button>
                    {selectedVoice && (
                      <VoiceChip
                        voice={selectedVoice}
                        index={voices.findIndex(v => v.voiceId === selectedVoice.voiceId)}
                        className='tw-ml-auto'
                      />
                    )}
                  </div>
                  <DottedBorder side='inline' className='md:tw-hidden tw-mb-3' />
                </div>

                <div className='tw-relative tw-flex-1 tw-overflow-y-auto tw-scroll-pt-4 tw-scroll-pb-6 tw-flex tw-flex-col tw-scrollbar-none'>
                  <div className='tw-relative tw-flex-auto tw-flex tw-flex-col'>
                    {playingState === "playing" && (
                      <TextHighlighter
                        className='tw-isolate tw-absolute tw-inset-0 tw-text-base/base tw-text-transparent'
                        characters={alignment?.characters ?? []}
                        displayCharacters={displayCharacters}
                        endTimes={alignment?.character_end_times_seconds ?? []}
                        mediaRef={audioElementRef}
                        startTimes={alignment?.character_start_times_seconds ?? []}
                        withBrackets
                        wordClassName='tw-rounded tw-px-0.5 -tw-mx-0.5 tw-transition'
                        wordStyle={{
                          backgroundColor: { highlighted: "oklch(0.97 0.001 106.424)" },
                          transitionDuration: { default: "0.15s", highlighted: "0s" },
                        }}
                      />
                    )}
                    <TextEditor
                      spellCheck={false}
                      aria-label={tTTS("widget.textarea_label")}
                      className='tw-flex-auto'
                      textClassName='tw-text-base/base tw-text-black tw-caret-focus tw-outline-none'
                      value={text}
                      onChange={text => handleTextChange(text)}
                      placeholder={placeholder}
                      maxLength={CHARACTER_LIMIT}
                    >
                      {text =>
                        text.split(/(\[[^\]\n]+\])/).map((part: string, index: number) =>
                          index % 2 === 0 ? (
                            part
                          ) : (
                            <span key={index} className='tw-text-gray-400'>
                              {part}
                            </span>
                          ),
                        )
                      }
                    </TextEditor>
                  </div>
                  <div className='tw-flex-none tw-sticky tw-bottom-0 tw-h-6 tw-bg-gradient-to-t tw-from-white' />
                  <CharacterCounter text={text} maxCharacters={CHARACTER_LIMIT} />
                </div>

                <div className='tw-relative tw-flex tw-items-center tw-justify-between tw-gap-2 tw-pt-2 md:tw-pt-4'>
                  <div className='tw-flex tw-items-center tw-gap-2'>
                    <LanguageSelect
                      language={language}
                      onLanguageChange={handleLanguageChange}
                      showSelectedLabel
                      sortedLanguages={sortedLanguages}
                      triggerSize='md'
                      showFlag
                      variant='simple'
                      triggerVariant='elevated'
                      overlayStyle
                    />
                    {downloadButton === "show" && (
                      <DownloadButton
                        audioElementRef={audioElementRef}
                        canDownload={!regenerate && !!text && !!completeAudioData}
                        completeAudioData={completeAudioData}
                        buttonVariant='elevated'
                        buttonSize='pill'
                        className='tw-ml-0'
                        onAfterDownload={() => {
                          if (
                            showSignUpPopoverOnDownload &&
                            !safeLocalStorage.getItem(LocalStorageKeys.TTS_SIGNUP_POPOVER_DISMISSED)
                          ) {
                            captureEvent(EventName.mw_tts_signup_popover_shown);
                            setIsSignUpPopoverOpen(true);
                          }
                          onAfterDownload?.();
                        }}
                      />
                    )}
                  </div>

                  <PlayButton
                    disabled={isPlayButtonDisabled}
                    onPlay={handlePlay}
                    playingState={playingState}
                    willRegenerate={regenerate}
                  />
                </div>
              </div>
            </div>

            <audio
              ref={audioElementRef}
              className='tw-hidden'
              onEnded={() => {
                setPlayingState("idle");
                setOmniboxIdle();
              }}
              onPause={() => {
                setPlayingState("paused");
                setOmniboxIdle();
              }}
              onPlay={() => {
                setPlayingState("playing");
                setOmniboxActive();
              }}
            />

            <LimitReachedOverlay
              show={showLimitReachedOverlay}
              title={tTTS("widget.quota_exceeded_title")}
              description={tTTS("widget.quota_exceeded_description")}
              eventProperties={{
                product: "tts",
                limit_type: "quota",
              }}
            />
          </OmniboxSimpleWrapper>
        </div>

        <div
          className={clsx(
            "tw-flex-shrink-0 tw-h-12 md:tw-hidden tw-pt-2 tw-px-2",
            shadow && "tw-z-10",
            mobileEditorOnly && "tw-hidden",
          )}
        >
          <LinkTracked
            asButton
            buttonVariants={{
              variant: "elevated",
              size: "md",
              expand: true,
            }}
            href={appUrls.voiceLibrary}
            eventName={EventName.mw_tts_voice_library_explore_clicked}
            eventProperties={{
              source_component: "voices_with_tts_omnibox",
            }}
          >
            {tVoices("explore_voices")}
          </LinkTracked>
        </div>
      </div>
      <TTSDownloadSignUpPopover
        isOpen={isSignUpPopoverOpen}
        onOpenChange={open => {
          setIsSignUpPopoverOpen(open);
          if (!open) {
            captureEvent(EventName.mw_tts_signup_popover_closed);
            safeLocalStorage.setItem(LocalStorageKeys.TTS_SIGNUP_POPOVER_DISMISSED, "1");
          }
        }}
      />
    </>
  );
}

export function VoicesWithTTS({ ref, ...props }: VoicesWithTTSProps & { ref?: Ref<VoicesWithTTSHandle> }) {
  return (
    <AudioProvider>
      <VoicesWithTTSInner ref={ref} {...props} />
    </AudioProvider>
  );
}
